import React, { useCallback, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { TransactionLine } from "../../../../docuclipper/DocuclipperTypes";
import {
  formikInput,
  isNullOrUndefined,
  useHasQbo,
} from "../../../../utils/utils";
import { ReduxState } from "../../../../redux";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Row, Col, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { uniq } from "lodash";
import EditableText from "../../../Docuclipper/EditableText";
import {
  addTagToTransactions,
  removeTagFromTransactions,
  updateJob,
  updateJobTransaction,
} from "../../../../docuclipper/api";
import Select from "react-select";
import { getCategoriesAction } from "../../../../redux/reducers/Categories";
import { fetchPieChart } from "../../../../redux/reducers/TagRegexes";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import AgTable from "../AnalyzeTransactionsTable/AgTable";
import { useTransactions } from "../AnalyzeTransactionsTable/hooks";
import CreatableAgGridCell, {
  CreatableAgGridCellRenderer,
} from "../../../../utils/ag-grid-utils";

import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createAlert } from "../../../../redux/reducers/Alerts";
import { fetchTls } from "../../../../redux/reducers/TransactionManager";

const TagForm = ({ onSubmit, cta }) => {
  // Validation Schema using Yup
  const validationSchema = Yup.object().shape({
    tag: Yup.string().required("Tag is required"),
  });

  // Initial values for the form
  const initialValues = {
    tag: "",
  };

  // Handle form submission
  //   const onSubmit = (values) => {
  //     console.log(values);
  //   };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        onSubmit(values);
        resetForm();
      }}
    >
      {({ errors, touched }) => (
        <Form>
          <Field
            className="ml-2"
            type="text"
            id="firstName"
            component={formikInput}
            name="tag"
          />
          {errors.tag && touched.tag ? <div>{errors.tag}</div> : null}
          <Button type="submit">{cta}</Button>
        </Form>
      )}
    </Formik>
  );
};

export const TaggingTransactionsTable = () => {
  const gridApiRef = useRef(null);

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    // You can also store columnApi if needed
    // const columnApi = params.columnApi;
  };

  const transactions = useTransactions();

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { tls } = useSelector((state: ReduxState) => state.TransactionManager);
  const { categories: tlsCategories } = tls;
  const dispatch = useDispatch();

  const [filterValues, setFilterValues] = useState([]);

  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);

  const onSelectionChanged = useCallback(
    (e) => {
      if (e.source === "api") {
        return;
      }

      if (gridApiRef && gridApiRef.current) {
        const selectedNodes = (gridApiRef.current as any).getSelectedNodes();
        const selectedIds = selectedNodes.map((node) => node.id);

        setSelectedRowIds(selectedIds);
      }
    },
    [gridApiRef]
  );

  React.useEffect(() => {
    // if (gridApi) {
    //   gridApi.deselectAll();
    // }

    if (gridApiRef && gridApiRef.current && selectedRowIds.length > 0) {
      (gridApiRef.current as any).forEachNode((node) => {
        if (selectedRowIds.includes(node.id) && !node.selected) {
          //   node.setSelected(true, false, true);
          node.setSelected(true);
        }
      });
    }
  }, [gridApiRef, selectedRowIds.length]);

  const performBulkOperation = (values, op: "addTag" | "removeTag") => {
    if (!gridApiRef || !gridApiRef.current) {
      return;
    }
    const selectedNodes = (gridApiRef.current as any).getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);
    // Perform your operation with selectedData

    const fn =
      op === "addTag"
        ? addTagToTransactions
        : op === "removeTag"
        ? removeTagFromTransactions
        : () => Promise.resolve();

    if (!job) {
      return;
    }
    fn(
      job?.id,
      values.tag,
      selectedData.map((x) => x.id)
    )
      .then(() => {
        dispatch(
          createAlert({
            message: `${selectedData.length} transactions updated successfully`,
            timeout: 0,
            type: "success",
          })
        );
        dispatch(fetchTls(false));
        setSelectedRowIds([]);
      })
      .catch((err) => {
        console.error(err);
        dispatch(
          createAlert({
            message: `Error updating tags`,
            timeout: 0,
            type: "error",
          })
        );
      });
  };

  const [selectedCount, setSelectedCount] = React.useState(0);
  const categories = uniq(transactions.map((x) => x.category));
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  React.useEffect(() => {
    setColumnDefs([
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
      },
      {
        field: "date",
        filter: "agDateColumnFilter",
        filterParams: datefilterParamsYYYYMMDD,
        sortable: true,
        flex: 1,
      },
      {
        field: "description",
        filter: "agTextColumnFilter",
        flex: 3,
        filterParams: {
          // pass in additional parameters to the Text Filter
        },
      },
      {
        field: "amount",
        filter: "agNumberColumnFilter",
        sortable: true,
        flex: 1,
        filterParams: {
          // pass in additional parameters to the Number Filter
        },
      },
      {
        field: "tags",
        filter: "agSetColumnFilter",
        flex: 2,
        cellRenderer: CreatableAgGridCellRenderer,
        // editable: true,
      },
    ]);
  }, [categories.length]);

  return (
    <div>
      {selectedRowIds.length > 0 && (
        <Row>
          <div>{selectedRowIds.length} rows selected</div>

          <TagForm
            cta="Add Tag"
            onSubmit={(values) => performBulkOperation(values, "addTag")}
          />
          <TagForm
            cta="Remove Tag"
            onSubmit={(values) => performBulkOperation(values, "removeTag")}
          />
        </Row>
      )}
      <AgTable
        reactiveCustomComponents
        hasExport={false}
        rowData={transactions}
        columnDefs={columnDefs}
        width={"100%"}
        height={400}
        rowSelection="multiple" // enable multiple row selection
        rowMultiSelectWithClick={true} // allow multiple row selection with clicks
        suppressRowClickSelection={true} // prevent row selection with clicks (optional)
        onSelectionChanged={onSelectionChanged} // callback for selection changes
        onGridReady={onGridReady}
      />
    </div>
  );
};
